import { ChangeDetectorRef, Injectable, OnDestroy, Pipe, PipeTransform, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionConfigService } from '../services/permission-config.service';
import { PermissionService } from '../services/permission.service';

/**
 * Shared change-detection wiring for the impure permission pipes: both must
 * re-render whenever permissions, the permission revision, or the config change.
 *
 * @Injectable is required — an undecorated abstract class using Angular
 * lifecycle hooks fails the build with NG2007.
 */
@Injectable()
abstract class PermissionAwarePipe implements OnDestroy {
  protected readonly permissionService = inject(PermissionService);
  protected readonly permissionConfigService = inject(PermissionConfigService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly subs: Subscription[];

  constructor() {
    const markForCheck = () => this.cdr.markForCheck();
    this.subs = [
      this.permissionService.userPermissions$.subscribe(markForCheck),
      this.permissionService.permissionsRevision$.subscribe(markForCheck),
      this.permissionConfigService.config$.subscribe(markForCheck),
    ];
  }

  ngOnDestroy(): void {
    for (const sub of this.subs) {
      sub.unsubscribe();
    }
  }
}

@Pipe({
  name: 'hasPermission',
  standalone: true,
  pure: false,
})
export class HasPermissionPipe extends PermissionAwarePipe implements PipeTransform {
  transform(permission: string | string[], requireAll = false, fallbackPermission?: string): boolean {
    this.permissionService.permissionRevision();
    const c = this.permissionConfigService.getConfig();
    if (!c.enablePermissionChecks || c.bypassAllPermissions) return true;
    if (!permission) return true;

    const list = Array.isArray(permission) ? permission : [permission];
    let ok = requireAll
      ? this.permissionService.hasAllPermissions(list)
      : this.permissionService.hasAnyPermission(list);
    if (!ok && fallbackPermission) {
      ok = this.permissionService.hasPermission(fallbackPermission);
    }
    return ok;
  }
}

@Pipe({
  name: 'hasRole',
  standalone: true,
  pure: false,
})
export class HasRolePipe extends PermissionAwarePipe implements PipeTransform {
  transform(role: string | string[], requireAll = false): boolean {
    this.permissionService.permissionRevision();
    const c = this.permissionConfigService.getConfig();
    if (!c.enablePermissionChecks || c.bypassAllPermissions) return true;
    if (!role) return true;
    const list = Array.isArray(role) ? role : [role];
    return requireAll
      ? this.permissionService.hasAllRoles(list)
      : this.permissionService.hasAnyRole(list);
  }
}
